import React, { useCallback, useEffect, useRef } from 'react';
import { TimecodeDisplay } from './TimecodeDisplay';
import { formatTimecode, timecodeToSeconds } from '../utils/timecodeFormatter';

interface TimecodePopoverProps {
  value: number;
  duration: number;
  fps: number;
  startFrame?: number;
  dropFrame?: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onSeek: (time: number) => void;
}

export function TimecodePopover({
  value,
  duration,
  fps,
  startFrame = 0,
  dropFrame = false,
  isOpen,
  onToggle,
  onSeek,
}: TimecodePopoverProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const currentTC = formatTimecode(value, fps, startFrame, dropFrame);

  // Create timecode-input imperatively so React doesn't re-render it
  // on every currentTime change (which would overwrite user edits)
  useEffect(() => {
    if (!isOpen || !popoverRef.current) return;

    const tcInput = document.createElement('timecode-input');
    tcInput.setAttribute('value', currentTC);
    tcInput.setAttribute('timebase', String(Math.round(fps)));
    if (dropFrame) tcInput.setAttribute('drop-frame', '1');
    // Insert before the Go button so the input appears first
    const placeholder = popoverRef.current.querySelector('.temp-placeholder');
    if (placeholder) {
      popoverRef.current.replaceChild(tcInput, placeholder);
    }

    // Focus the hours field inside the shadow DOM
    const focusTimer = setTimeout(() => {
      const hours = tcInput.shadowRoot?.querySelector('#hours') as HTMLInputElement | null;
      hours?.focus();
      hours?.select();
    }, 50);

    return () => {
      clearTimeout(focusTimer);
      tcInput.remove();
    };
    // Only run when the popover opens/closes — NOT when currentTC changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onToggle();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen, onToggle]);

  // Seek to the timecode currently entered in the popover input
  const seekToPopoverValue = useCallback(() => {
    const tcInput = popoverRef.current?.querySelector('timecode-input');
    const tc = tcInput?.getAttribute('value');
    if (tc && onSeek) {
      const seconds = timecodeToSeconds(tc, fps, startFrame, dropFrame);
      if (seconds >= 0 && seconds <= duration) {
        onSeek(seconds);
      }
    }
    onToggle();
  }, [fps, startFrame, dropFrame, duration, onSeek, onToggle]);

  // Handle Enter (seek) and Escape (close) in the popover
  const handlePopoverKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        seekToPopoverValue();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }
    },
    [seekToPopoverValue, onToggle],
  );

  return (
    <div ref={containerRef} className="kollab-player-timecode-container">
      <TimecodeDisplay
        value={value}
        fps={fps}
        startFrame={startFrame}
        dropFrame={dropFrame}
        onClick={onToggle}
      />
      {isOpen && (
        <div
          ref={popoverRef}
          className="kollab-player-timecode-popover"
          onKeyDown={handlePopoverKeyDown}
        >
          <h3>Seek to Timecode</h3>
          <div className="temp-placeholder"></div>
          <button
            type="button"
            className="kollab-player-timecode-popover-btn actionbtn"
            onClick={seekToPopoverValue}
            aria-label="Go to timecode"
          >
            Go
          </button>
        </div>
      )}
    </div>
  );
}
